import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Empty, Input, List, Modal, Popconfirm, Select, Space, Tag, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { toast } from 'sonner';
import { usoSistemaService } from '../../services/usoSistemaService';
import { Colaborador, UsoConta, UsoPessoa } from '../../types/usoSistema';

const { Text } = Typography;

interface ColaboradoresModalProps {
  open: boolean;
  tenantId: number | null;
  pessoas: UsoPessoa[];
  onClose: () => void;
  onSalvo: () => void;
}

interface Edicao {
  id?: string;
  nome: string;
  profileIds: string[];
}

/** Agrupa manualmente as várias contas (uma por papel) de uma mesma pessoa */
export function ColaboradoresModal({ open, tenantId, pessoas, onClose, onSalvo }: ColaboradoresModalProps) {
  const [edicao, setEdicao] = useState<Edicao | null>(null);
  const [salvando, setSalvando] = useState(false);

  const { data: colaboradores = [], refetch, isLoading } = useQuery({
    queryKey: ['colaboradores-uso'],
    queryFn: usoSistemaService.listarColaboradores,
    enabled: open,
  });

  useEffect(() => {
    if (!open) setEdicao(null);
  }, [open]);

  const contas = useMemo(() => {
    const mapa = new Map<string, UsoConta>();
    pessoas.forEach(p => p.contas.forEach(c => mapa.set(c.profile_id, c)));
    return mapa;
  }, [pessoas]);

  const donoDaConta = useMemo(() => {
    const mapa = new Map<string, Colaborador>();
    colaboradores.forEach(c => c.profile_ids.forEach(id => mapa.set(id, c)));
    return mapa;
  }, [colaboradores]);

  // Contas já vinculadas a outra pessoa não aparecem: cada conta pertence a uma só pessoa
  const opcoesContas = useMemo(
    () =>
      [...contas.values()]
        .filter(c => {
          const dono = donoDaConta.get(c.profile_id);
          return !dono || dono.id === edicao?.id;
        })
        .sort((a, b) => a.nome.localeCompare(b.nome))
        .map(c => ({
          value: c.profile_id,
          label: `${c.nome} (${c.username ?? 'sem usuário'} · ${c.role ?? '—'})`,
        })),
    [contas, donoDaConta, edicao?.id]
  );

  const salvar = async () => {
    if (!edicao || !tenantId) return;
    if (!edicao.nome.trim()) {
      toast.error('Informe o nome da pessoa');
      return;
    }
    setSalvando(true);
    try {
      await usoSistemaService.salvarColaborador({
        id: edicao.id,
        nome: edicao.nome.trim(),
        tenantId,
        profileIds: edicao.profileIds,
      });
      toast.success('Pessoa salva');
      setEdicao(null);
      await refetch();
      onSalvo();
    } catch (error) {
      toast.error(`Erro ao salvar: ${(error as Error).message}`);
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (id: string) => {
    try {
      await usoSistemaService.excluirColaborador(id);
      await refetch();
      onSalvo();
    } catch (error) {
      toast.error(`Erro ao excluir: ${(error as Error).message}`);
    }
  };

  return (
    <Modal
      title="Agrupar contas por pessoa"
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      className="colaboradores-modal"
      destroyOnHidden
    >
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="Quando uma pessoa tem mais de uma conta (uma por papel), agrupe-as aqui para que o relatório some o uso de todas. Contas não agrupadas aparecem sozinhas."
      />

      {edicao ? (
        <Space orientation="vertical" style={{ width: '100%' }} size={12}>
          <Input
            placeholder="Nome da pessoa"
            value={edicao.nome}
            onChange={e => setEdicao({ ...edicao, nome: e.target.value })}
            autoFocus
          />
          <Select
            mode="multiple"
            placeholder="Contas desta pessoa"
            style={{ width: '100%' }}
            value={edicao.profileIds}
            onChange={profileIds => setEdicao({ ...edicao, profileIds })}
            options={opcoesContas}
            optionFilterProp="label"
          />
          <Space>
            <Button type="primary" onClick={salvar} loading={salvando}>
              Salvar
            </Button>
            <Button onClick={() => setEdicao(null)}>Cancelar</Button>
          </Space>
        </Space>
      ) : (
        <>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            block
            className="mb-3"
            onClick={() => setEdicao({ nome: '', profileIds: [] })}
          >
            Nova pessoa
          </Button>
          <List
            loading={isLoading}
            dataSource={colaboradores}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhuma pessoa agrupada ainda" /> }}
            renderItem={c => (
              <List.Item
                actions={[
                  <Button
                    key="editar"
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => setEdicao({ id: c.id, nome: c.nome, profileIds: c.profile_ids })}
                  />,
                  <Popconfirm
                    key="excluir"
                    title="Desfazer este agrupamento?"
                    description="As contas voltam a aparecer separadas. Nenhum dado de uso é apagado."
                    onConfirm={() => excluir(c.id)}
                  >
                    <Button type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  title={c.nome}
                  description={
                    c.profile_ids.length ? (
                      <Space size={4} wrap>
                        {c.profile_ids.map(id => (
                          <Tag key={id} bordered={false}>
                            {contas.get(id)?.username ?? 'conta inativa'}
                          </Tag>
                        ))}
                      </Space>
                    ) : (
                      <Text type="secondary">Sem contas vinculadas</Text>
                    )
                  }
                />
              </List.Item>
            )}
          />
        </>
      )}
    </Modal>
  );
}
